import { useEffect, useState, useCallback } from "react";
import { Shield, Users, Coins, Gavel, CheckCircle2, XCircle, ScrollText, Settings2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [reports, setReports] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [logs, setLogs] = useState([]);
  const [founding, setFounding] = useState(null);
  const [commission, setCommission] = useState("");

  const load = useCallback(() => {
    api.adminStats().then(({ data }) => setStats(data)).catch(() => {});
    api.adminExperts("pending").then(({ data }) => setQueue(data)).catch(() => {});
    api.adminReports().then(({ data }) => setReports(data)).catch(() => {});
    api.adminPayouts().then(({ data }) => setPayouts(data)).catch(() => {});
    api.adminPayments().then(({ data }) => setPayments(data)).catch(() => {});
    api.adminAuditLogs().then(({ data }) => setLogs(data)).catch(() => {});
    api.adminFounding().then(({ data }) => setFounding(data)).catch(() => {});
    api.adminSettings().then(({ data }) => setCommission(String(data.commission_rate))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { await api.adminApprove(id); toast.success("Expert approuvé"); load(); };
  const reject = async (id) => { await api.adminReject(id); toast.info("Expert refusé"); load(); };
  const resolve = async (id) => { await api.adminResolveReport(id, "Traité par la modération"); toast.success("Signalement résolu"); load(); };
  const processPayout = async (id, action) => { await api.adminProcessPayout(id, action); toast.success("Retrait mis à jour"); load(); };
  const revokeFounding = async (id) => { await api.adminSetFounding(id, "revoke"); toast.info("Statut fondateur retiré"); load(); };
  const saveCommission = async () => {
    try { await api.adminUpdateSettings({ commission_rate: parseFloat(commission) }); toast.success("Commission mise à jour"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doRefund = async (r) => {
    const c = await api.adminConsultations().then(({ data }) => data.find((x) => x.id === r.consultation_id));
    if (!c) { toast.error("Consultation liée introuvable"); return; }
    try { await api.adminRefund({ user_id: c.consultant_id, amount: c.credits_spent || 5, reason: "Litige résolu", consultation_id: c.id }); toast.success("Crédits remboursés au consultant"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const S = [
    { icon: Users, label: "Utilisateurs", val: stats?.users },
    { icon: Shield, label: "Experts actifs", val: stats?.experts },
    { icon: Gavel, label: "Signalements ouverts", val: stats?.open_reports },
    { icon: Coins, label: "Revenus (€ test)", val: stats?.revenue_eur },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <h1 className="flex items-center gap-2 font-serif text-3xl text-celeste-ivoire"><Shield className="h-7 w-7 text-celeste-or" /> Administration</h1>

      <div className="mt-6 grid gap-4 sm:grid-cols-4">
        {S.map((s, i) => (
          <div key={i} className="glass rounded-3xl p-5"><s.icon className="h-6 w-6 text-celeste-or" /><p className="mt-2 font-serif text-2xl text-celeste-ivoire">{s.val ?? "…"}</p><p className="text-xs text-celeste-lilas/70">{s.label}</p></div>
        ))}
      </div>

      <Tabs defaultValue="validation" className="mt-8">
        <TabsList className="flex-wrap bg-celeste-night/50">
          <TabsTrigger value="validation" data-testid="admin-tab-validation" className="data-[state=active]:bg-celeste-violet/30">Validation experts</TabsTrigger>
          <TabsTrigger value="moderation" data-testid="admin-tab-moderation" className="data-[state=active]:bg-celeste-violet/30">Modération & litiges</TabsTrigger>
          <TabsTrigger value="finances" data-testid="admin-tab-finances" className="data-[state=active]:bg-celeste-violet/30">Finances</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings" className="data-[state=active]:bg-celeste-violet/30">Réglages</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit" className="data-[state=active]:bg-celeste-violet/30">Journal</TabsTrigger>
        </TabsList>

        <TabsContent value="validation" className="mt-4">
          <div className="glass rounded-3xl p-4" data-testid="expert-queue">
            {queue.length === 0 ? <p className="p-6 text-center text-sm text-celeste-lilas/60">Aucune candidature en attente.</p> :
              queue.map((e) => (
                <div key={e.id} className="flex items-center justify-between border-b border-celeste-violet/15 py-3 last:border-0">
                  <div><p className="text-celeste-ivoire">{e.professional_name}</p><p className="text-xs text-celeste-lilas/60">{e.title} · {e.specialties?.join(", ")}</p></div>
                  <div className="flex gap-2">
                    <Button data-testid={`approve-${e.id}`} size="sm" className="gold-btn" onClick={() => approve(e.id)}><CheckCircle2 className="mr-1 h-4 w-4" /> Approuver</Button>
                    <Button size="sm" variant="ghost" className="border border-red-400/40 text-red-200" onClick={() => reject(e.id)}><XCircle className="mr-1 h-4 w-4" /> Refuser</Button>
                  </div>
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="moderation" className="mt-4">
          <div className="glass rounded-3xl p-4" data-testid="reports-list">
            {reports.length === 0 ? <p className="p-6 text-center text-sm text-celeste-lilas/60">Aucun signalement.</p> :
              reports.map((r) => (
                <div key={r.id} className="flex items-center justify-between border-b border-celeste-violet/15 py-3 last:border-0">
                  <div>
                    <p className="text-celeste-ivoire">{r.type === "litige" ? "Litige" : "Signalement"} · {r.reason}</p>
                    <p className="text-xs text-celeste-lilas/60">{r.description} · {r.status}</p>
                  </div>
                  <div className="flex gap-2">
                    {r.type === "litige" && r.status === "open" && <Button size="sm" variant="ghost" className="border border-celeste-or/40 text-celeste-or" onClick={() => doRefund(r)}>Rembourser</Button>}
                    {r.status === "open" && <Button data-testid={`resolve-${r.id}`} size="sm" className="gold-btn" onClick={() => resolve(r.id)}>Résoudre</Button>}
                  </div>
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="finances" className="mt-4 space-y-6">
          <div className="glass rounded-3xl p-4">
            <h3 className="mb-3 font-serif text-celeste-ivoire">Demandes de retrait</h3>
            {payouts.length === 0 ? <p className="text-sm text-celeste-lilas/60">Aucune demande.</p> :
              payouts.map((p) => (
                <div key={p.id} className="flex items-center justify-between border-b border-celeste-violet/15 py-2 last:border-0 text-sm">
                  <span className="text-celeste-ivoire">{p.amount_credits} cr · {p.status}</span>
                  {p.status === "requested" && (
                    <div className="flex gap-2">
                      <Button size="sm" className="gold-btn" onClick={() => processPayout(p.id, "approve")}>Approuver</Button>
                      <Button size="sm" variant="ghost" className="border border-red-400/40 text-red-200" onClick={() => processPayout(p.id, "reject")}>Refuser</Button>
                    </div>
                  )}
                </div>
              ))}
          </div>
          <div className="glass rounded-3xl p-4">
            <h3 className="mb-3 font-serif text-celeste-ivoire">Transactions (Stripe test)</h3>
            {payments.length === 0 ? <p className="text-sm text-celeste-lilas/60">Aucune transaction.</p> :
              payments.slice(0, 12).map((p) => (
                <div key={p.session_id} className="flex justify-between border-b border-celeste-violet/15 py-2 last:border-0 text-sm">
                  <span className="text-celeste-ivoire">{p.lookup_key} · {p.credits} cr</span>
                  <span className="text-celeste-lilas/60">{(p.amount / 100).toFixed(2)} € · {p.payment_status}</span>
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <div className="glass max-w-md rounded-3xl p-6" data-testid="admin-settings">
            <h3 className="flex items-center gap-2 font-serif text-celeste-ivoire"><Settings2 className="h-4 w-4 text-celeste-or" /> Commission plateforme</h3>
            <p className="mt-1 text-xs text-celeste-lilas/60">Part plateforme par consultation (0 à 0.9). Ex : 0.55 = 55 % plateforme. Praticien standard 45 %, fondateurs 55 %.</p>
            <div className="mt-4 flex gap-2">
              <Input data-testid="commission-input" type="number" step="0.05" value={commission} onChange={(e) => setCommission(e.target.value)} className="bg-celeste-night/50 border-celeste-violet/30 text-celeste-ivoire" />
              <Button data-testid="save-commission" onClick={saveCommission} className="gold-btn font-semibold">Enregistrer</Button>
            </div>
          </div>
          <div className="glass mt-6 rounded-3xl p-6" data-testid="founding-admin">
            <h3 className="font-serif text-celeste-ivoire">Offre fondateurs — 20 places · 55 % reversés</h3>
            {founding && <p className="mt-1 text-xs text-celeste-lilas/60">Attribuées : {founding.granted_count}/20 · Restantes : {founding.remaining}. Une place libérée n'est pas réattribuée automatiquement (décision propriétaire requise).</p>}
            <div className="mt-3 space-y-2">
              {(founding?.members || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between text-sm">
                  <span className="text-celeste-ivoire">{m.professional_name} · {Math.round((m.founding_share || 0) * 100)} %{m.is_demo ? " (démo)" : ""}</span>
                  <Button data-testid={`revoke-founding-${m.id}`} size="sm" variant="ghost" className="border border-red-400/40 text-red-200" onClick={() => revokeFounding(m.id)}>Retirer</Button>
                </div>
              ))}
              {founding && founding.members.length === 0 && <p className="text-sm text-celeste-lilas/60">Aucun membre fondateur pour l'instant.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="glass rounded-3xl p-4" data-testid="audit-logs">
            <h3 className="mb-3 flex items-center gap-2 font-serif text-celeste-ivoire"><ScrollText className="h-4 w-4 text-celeste-or" /> Journal des accès administratifs</h3>
            {logs.length === 0 ? <p className="text-sm text-celeste-lilas/60">Aucune entrée.</p> :
              logs.slice(0, 30).map((l) => (
                <div key={l.id} className="border-b border-celeste-violet/15 py-2 text-xs text-celeste-lilas/70 last:border-0">
                  <span className="text-celeste-or">{l.action}</span> · {l.target} · {new Date(l.created_at).toLocaleString("fr-FR")}
                </div>
              ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
